import React, { useState } from "react";
import { inject, observer } from "mobx-react";
import { Link as RouterLink } from "react-router-dom";
import {
  fn_bridge_agency_popup,
  fn_bridge_agency_video,
  fn_baemin_install,
  fn_bridge_connect_video,
} from "function/FnPosBridge";
import { fn_open_naver_setting } from "function/FnBodySetting";
import { partnerIcon, analyticeEvent } from "utils/Utils";
import { ORDER_CONNECT_CONFIGS } from "views/setting/constants/OrderConnectConfig";
import SegmentedControl from "components/ui/SegmentedControl";
import { SectionTitle } from "components/ui/SectionTitle";
import brandChannelList from "constants/order/brandChannelConfig";
import modalStore from "stores/ModalStore";

/**
 * 연동 버튼 컴포넌트
 * @param isBasic 기본 연동 여부
 * @param unlinked 연동 해제 여부
 * @param name 채널 이름
 * @param iconName 채널 아이콘
 * @param subTitle 채널 설명
 * @param type 채널 타입
 * @param onClick 클릭 이벤트
 * @param isExpanded 확장 여부
 */

const Link = RouterLink as any;
const LinkContainer = ({
  isBasic = false,
  unlinked = false,
  name,
  iconName,
  subTitle,
  type,
  onClick,
  isExpanded = false,
}: {
  isBasic?: boolean;
  unlinked?: boolean;
  name: string;
  iconName?: string;
  subTitle: string;
  type: string;
  onClick?: () => void;
  isExpanded?: boolean;
}) => {
  const connectText = (type: string) => {
    switch (type) {
      case "login":
        return "로그인 연동";
      case "print":
        return "출력물 연동";
      case "order":
        return "주문접수 연동";
      case "none":
        return "미연동";
      default:
        return "미연동";
    }
  };

  const renderButton = () => {
    if (isBasic) {
      return <></>;
    }

    if (unlinked) {
      return (
        <button className="btn" onClick={onClick}>
          연동하기
        </button>
      );
    }
    return (
      <button className="detail" onClick={onClick}>
        <p>상세보기</p>
        <div className={isExpanded ? "arrow_up_ico" : "arrow_down_ico"}></div>
      </button>
    );
  };
  return (
    <div className={`list ${name === "요기요" ? "ygy" : ""}`}>
      <div className="left">
        <div className="ico">
          <img src={partnerIcon(iconName ? iconName : name)} alt={name} width="36" height="36" />
        </div>
        <div className="title">
          <div className="txt">
            <p>
              <strong>{name}</strong>
            </p>
            <span className={type}>{connectText(type)}</span>
          </div>
          <p className="sub_txt">{subTitle}</p>
        </div>
        {renderButton()}
      </div>
    </div>
  );
};

/**
 * 가이드 컴포넌트
 * @param isStep 스텝 여부
 * @param stepNo 스텝 번호
 * @param onClick 클릭 이벤트
 * @param title 제목
 * @param subTitle 서브 타이틀
 * @param btnText 버튼 텍스트
 * @param iconName 아이콘 이름
 */
const GuideRow = ({
  isStep = false,
  stepNo = null,
  onClick,
  title,
  subTitle,
  btnText,
  iconName,
}: {
  isStep?: boolean;
  stepNo?: number | null;
  onClick: () => void;
  title: string;
  subTitle: string;
  btnText: string;
  iconName: string;
}) => {
  return (
    <div className="wraper">
      <div className="txt_wrap">
        <div className="first_wrap">
          {isStep && <span className="step">STEP {stepNo}</span>}
          <span className="txt">
            <strong>{title}</strong>
          </span>
        </div>
        <div className="sub_txt">{subTitle}</div>
      </div>
      <div className="right">
        <button className="second_btn">
          <span onClick={onClick}>{btnText}</span>
          <span className={iconName}></span>
        </button>
      </div>
    </div>
  );
};

/**
 * 사이트 바로가기 컴포넌트
 * @param name 채널 이름
 * @param onClick 클릭 이벤트
 */
const GotoSiteContainer = ({ id, name, onClick }: { id: string; name: string; onClick: () => void }) => {
  const text = name === "네이버" ? "파트너센터" : "사장님 사이트";

  const hasSite = ["baemin", "ygy", "ddangyo", "muk", "wmpo", "naver", "panda", "oltteaah", "naverBooking"];

  if (!hasSite.includes(id)) {
    return null;
  }

  return (
    <div className="box">
      <p>
        <strong>
          {name} {text} 바로가기
        </strong>
      </p>
      <button className="third_btn">
        <span onClick={onClick}>사장님 사이트</span>
        <div className="exit_ico"></div>
      </button>
    </div>
  );
};

const SettingDefaultOrderView = ({ rootStore }: { rootStore: any }) => {
  const { layerStore, settingStore, searchStore } = rootStore;
  const { getShopApiSetting } = settingStore;
  const isPaymentEnabled = settingStore.getPayFreeType.paymentUseYn === "2";
  // 상세보기 확장 아이디
  const [expandedId, setExpandedId] = useState<string[]>([]);
  const [controlType, setControlType] = useState<"basic" | "advanced">("advanced");

  // 상세보기 확장 처리
  const handleExpanded = (itemId: string) => {
    setExpandedId(prevId => (prevId.includes(itemId) ? prevId.filter(id => id !== itemId) : [...prevId, itemId]));
  };

  // 연동된 제휴사 채널 리스트
  const paymentChannelList = brandChannelList.filter(item => getShopApiSetting[item.connectedId] === "Y");

  // 배달의 민족 연동
  const isBaeminUse =
    settingStore.getBaeminUseStatus.isCurrentPosBaeminUse === "Y" &&
    searchStore.getSession.connPosNo === settingStore.getBaeminUseStatus.baeminUsePosNo;

  const showModal = (type: string) => {
    layerStore.setDlvConnectTypeView(type);
    layerStore.setLayerConnectView(true);
  };

  return (
    <div className="scroll tab">
      <div className="inner">
        <div className="wrap_appConnect !pt-0">
          <SectionTitle
            title="배달앱 연동 설정"
            subTitle="배달매니저에서 주문 접수할 수 있는 채널 관리"
            addBottomBorder
          >
            <SegmentedControl
              options={[
                {
                  label: "고급",
                  value: "advanced",
                  checked: controlType === "advanced",
                  onClick: () => setControlType("advanced"),
                },
                {
                  label: "기본",
                  value: "basic",
                  checked: controlType === "basic",
                  onClick: () => setControlType("basic"),
                },
              ]}
            />
          </SectionTitle>
          {/* </div> */}

          {/* 리스트 */}
          {controlType === "advanced" ? (
            isPaymentEnabled ? (
              <div className="lists">
                {/* 배민 */}
                {isBaeminUse ? (
                  <>
                    <div>
                      <LinkContainer
                        name="배달의민족"
                        subTitle="별도 프로그램에 로그인하면 배달매니저에서 주문을 접수할 수 있어요"
                        type="login"
                        onClick={() => handleExpanded("baemin")}
                        isExpanded={expandedId.includes("baemin")}
                      />
                      {/* 상세보기 클릭시 */}
                      {expandedId.includes("baemin") && (
                        <div className="container">
                          <GuideRow
                            title="배민주문접수 PC Lite 설치하기"
                            subTitle="설치 방법 확인 후 설치해주세요"
                            btnText="설치하기"
                            iconName="download_ico"
                            onClick={() => {
                              fn_baemin_install(rootStore);
                            }}
                            isStep={true}
                            stepNo={1}
                          />
                          <GuideRow
                            title="사용법 영상 시청하기"
                            subTitle="사용 매뉴얼 영상 시청 후 이용해주세요"
                            btnText="사용법 영상 보기"
                            iconName="video_ico"
                            onClick={() => {
                              analyticeEvent("setting", "settingBaeminOperationManual", searchStore.getShopCd);
                              fn_bridge_agency_video(rootStore, "baemin");
                            }}
                            isStep={true}
                            stepNo={2}
                          />
                          <GotoSiteContainer
                            id="baemin"
                            name="배달의민족"
                            onClick={() => {
                              fn_bridge_agency_popup(rootStore, "baemin");
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <LinkContainer
                    name="배달의민족"
                    subTitle="주문전표가 출력되면 배달매니저에 주문이 생성돼요"
                    type="print"
                    onClick={() => showModal("BMN")}
                    unlinked={true}
                  />
                )}

                {/* 요기요 */}
                {settingStore.getYgyUseStatus === "Y" ? (
                  <div>
                    <LinkContainer
                      name="요기요"
                      subTitle="배달매니저에서 주문을 바로 접수 & 처리할 수 있어요"
                      type="order"
                      onClick={() => handleExpanded("ygy")}
                      isExpanded={expandedId.includes("ygy")}
                    />
                    {expandedId.includes("ygy") && (
                      <div className="container">
                        <GuideRow
                          onClick={() => {
                            fn_bridge_agency_video(rootStore, "ygySet");
                          }}
                          title="필수 세팅하기"
                          subTitle="프린터 알림음 등 필수 설정을 확인해주세요"
                          btnText="필수 세팅"
                          iconName="video_ico"
                        />
                        <GuideRow
                          onClick={() => {
                            fn_bridge_agency_video(rootStore, "ygy");
                          }}
                          title="주문접수 영상보기"
                          subTitle="요기요 주문 접수 영상을 꼭 확인해주세요"
                          btnText="주문접수 영상"
                          iconName="video_ico"
                        />
                        <GuideRow
                          onClick={() => {
                            layerStore.setDlvConnectTypeView("YGY");
                            modalStore.openModal("DeliveryConnectBusiness", {});
                          }}
                          title="요기요 연동 설정 관리"
                          subTitle="요기요 가게 연동을 추가하거나 해제할 수 있어요"
                          btnText="연동설정"
                          iconName="setting_ico"
                        />
                        <GotoSiteContainer
                          id="ygy"
                          name="요기요"
                          onClick={() => {
                            fn_bridge_agency_popup(rootStore, "ygy");
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <LinkContainer
                      name="요기요"
                      subTitle="주문전표가 출력되면 배달매니저에 주문이 생성돼요"
                      type="print"
                      unlinked={true}
                      onClick={() => {
                        showModal("YGY");
                        analyticeEvent("setting", "settingYogiyoMapping", searchStore.getShopCd);
                      }}
                    />
                  </>
                )}

                {/* 땡겨요 */}
                {settingStore.getShopApiSetting.ddangyoYn === "Y" ? (
                  <>
                    <div>
                      <LinkContainer
                        name="땡겨요"
                        subTitle="배달매니저에서 주문을 바로 접수 & 처리할 수 있어요"
                        type="order"
                        onClick={() => handleExpanded("ddangyo")}
                        isExpanded={expandedId.includes("ddangyo")}
                      />
                      {expandedId.includes("ddangyo") && (
                        <div className="container">
                          <GuideRow
                            onClick={() => {
                              fn_bridge_agency_video(rootStore, "ygySet");
                            }}
                            title="필수 세팅하기"
                            subTitle="프린터 알림음 등 필수 설정을 확인해주세요"
                            btnText="필수 세팅"
                            iconName="video_ico"
                          />
                          <GuideRow
                            onClick={() => {
                              fn_bridge_agency_video(rootStore, "ygy");
                            }}
                            title="주문접수 영상보기"
                            subTitle="(요기요 주문접수 방법과 동일) 꼭 확인해주세요"
                            btnText="주문접수 영상"
                            iconName="video_ico"
                          />
                          <GuideRow
                            onClick={() => {
                              layerStore.setDlvConnectTypeView("DDY");
                              modalStore.openModal("DeliveryConnectBusiness", {});
                            }}
                            title="땡겨요 연동 설정 관리"
                            subTitle="땡겨요 가게 연동을 추가하거나 해제할 수 있어요"
                            btnText="연동설정"
                            iconName="setting_ico"
                          />
                          <GotoSiteContainer
                            id="ddangyo"
                            name="땡겨요"
                            onClick={() => {
                              fn_bridge_agency_popup(rootStore, "ddangyo");
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <LinkContainer
                    name="땡겨요"
                    subTitle="주문전표가 출력되면 배달매니저에 주문이 생성돼요"
                    type="print"
                    onClick={() => {
                      showModal("DDY");
                      analyticeEvent("setting", "settingDdangyoMapping", searchStore.getShopCd);
                    }}
                    unlinked={true}
                  />
                )}

                {/* 네이버예약*/}
                {settingStore.getShopApiSetting.naverBookingYn === "Y" ? (
                  <div>
                    <LinkContainer
                      name="네이버"
                      subTitle="배달매니저에서 주문을 바로 접수 & 처리할 수 있어요 "
                      type="order"
                      iconName="NAVERBOOKING"
                      onClick={() => handleExpanded("naverBooking")}
                      isExpanded={expandedId.includes("naverBooking")}
                    />
                    {expandedId.includes("naverBooking") && (
                      <div className="container">
                        <GuideRow
                          onClick={() => {
                            fn_bridge_agency_video(rootStore, "default");
                            analyticeEvent("setting", "preSettingGuide", searchStore.getShopCd);
                          }}
                          title="사전 세팅 후 사용하기"
                          subTitle="주문 접수 전 꼭 확인해주세요"
                          btnText="필수 세팅"
                          iconName="video_ico"
                        />
                        <GuideRow
                          onClick={() => {
                            fn_bridge_agency_video(rootStore, "naverBooking");
                          }}
                          title="네이버 주문접수 사용하기"
                          subTitle="주문 접수 전 사용 방법을 숙지해주세요"
                          btnText="주문접수 영상"
                          iconName="video_ico"
                        />
                        <GuideRow
                          onClick={() => showModal("NAVERBOOKING")}
                          title="네이버 연동 설정 관리"
                          subTitle="네이버 매장 셀프 연동 관리하세요"
                          btnText="연동설정"
                          iconName="setting_ico"
                        />
                        <GotoSiteContainer
                          id="naverBooking"
                          name="네이버"
                          onClick={() => {
                            fn_bridge_agency_popup(rootStore, "naverBooking");
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : 
                (<LinkContainer
                    name="네이버"
                    subTitle="배달매니저에서 바로 주문을 접수하려면 [연동하기]를 진행해주세요"
                    iconName="NAVERBOOKING"
                    type="none"
                    onClick={() => {
                      showModal("NAVERBOOKING");
                    }}
                    unlinked={true}
                  />)
                }

                {/* 먹깨비 */}
                {settingStore.getShopApiSetting.mukkebiYn === "Y" ? (
                  <div>
                    <LinkContainer
                      name="먹깨비"
                      subTitle="배달매니저에서 주문을 바로 접수 & 처리할 수 있어요"
                      type="order"
                      onClick={() => handleExpanded("muk")}
                      isExpanded={expandedId.includes("muk")}
                    />
                    {expandedId.includes("muk") && (
                      <div className="container">
                        <GuideRow
                          title="먹깨비 연동 해제 신청"
                          subTitle="연동 해제가 필요한 경우 이용해주세요"
                          onClick={() => {
                            layerStore.setDlvConnectTypeView("MKB");
                            modalStore.openModal("DeliveryConnectBusiness", {});

                            layerStore.setMappingAction(false);
                          }}
                          btnText="해제신청"
                          iconName="disconnect_ico"
                        />
                        <GotoSiteContainer
                          id="muk"
                          name="먹깨비"
                          onClick={() => {
                            fn_bridge_agency_popup(rootStore, "muk");
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <LinkContainer
                    name="먹깨비"
                    subTitle="주문전표가 출력되면 배달매니저에 주문이 생성돼요"
                    type="print"
                    onClick={() => {
                      showModal("MKB");
                    }}
                    unlinked={true}
                  />
                )}

                {/* 위메프오 */}
                {settingStore.getShopApiSetting.wmpoYn === "Y" ? (
                  <div>
                    <LinkContainer
                      name="위메프오"
                      subTitle="배달매니저에서 주문을 바로 접수 & 처리할 수 있어요"
                      type="order"
                      onClick={() => handleExpanded("wmpo")}
                      isExpanded={expandedId.includes("wmpo")}
                    />
                    {expandedId.includes("wmpo") && (
                      <div className="container">
                        <GuideRow
                          title="위메프오 연동 해제 신청"
                          subTitle="연동 해제가 필요한 경우 이용해주세요"
                          onClick={() => {
                            layerStore.setDlvConnectTypeView("WMP");
                            modalStore.openModal("DeliveryConnectBusiness", {});
                            layerStore.setMappingAction(false);
                          }}
                          btnText="해제신청"
                          iconName="disconnect_ico"
                        />
                        <GotoSiteContainer
                          id="wmpo"
                          name="위메프오"
                          onClick={() => {
                            fn_bridge_agency_popup(rootStore, "wmpo");
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <LinkContainer
                    name="위메프오"
                    subTitle="주문전표가 출력되면 배달매니저에 주문이 생성돼요"
                    type="print"
                    onClick={() => {
                      showModal("WMP");
                    }}
                    unlinked={true}
                  />
                )}

                {/* 네이버 */}
                {/* {settingStore.getShopApiSetting.naverYn === "Y" && (
                  <>
                    <LinkContainer
                      iconName="NAVER"
                      name="네이버"
                      subTitle="배달매니저에서 주문을 바로 접수 & 처리할 수 있어요"
                      type="order"
                      onClick={() => handleExpanded("naver")}
                      isExpanded={expandedId.includes("naver")}
                    />
                    {expandedId.includes("naver") && (
                      <div className="container">
                        <GuideRow
                          title="네이버 연동 설정 관리"
                          subTitle="네이버 매장 셀프 연동 관리하세요"
                          onClick={() => fn_open_naver_setting(rootStore)}
                          btnText="연동설정"
                          iconName="setting_ico"
                        />
                        <GotoSiteContainer
                          id="naver"
                          name="네이버"
                          onClick={() => {
                            fn_bridge_agency_popup(rootStore, "naver");
                          }}
                        />
                      </div>
                    )}
                  </>
                )} */}

                {/* 헝그리판다 */}
                {settingStore.getShopApiSetting.hungryPandaYn === "Y" ? (
                  <>
                    <LinkContainer
                      iconName="pandaself"
                      name="헝그리판다"
                      subTitle="별도 프로그램에 로그인하면 배달매니저에서 주문을 접수할 수 있어요 "
                      type="order"
                      onClick={() => handleExpanded("panda")}
                      isExpanded={expandedId.includes("panda")}
                    />
                    {expandedId.includes("panda") && (
                      <div className="container">
                        <GuideRow
                          title="헝그리판다 연동 해제 신청"
                          subTitle="연동 해제가 필요한 경우 이용해주세요"
                          onClick={() => {
                            layerStore.setDlvConnectTypeView("HPD");
                            modalStore.openModal("DeliveryConnectBusiness", {});
                            layerStore.setMappingAction(false);
                          }}
                          btnText="해제신청"
                          iconName="disconnect_ico"
                        />
                        <GotoSiteContainer
                          id="panda"
                          name="헝그리판다"
                          onClick={() => {
                            fn_bridge_agency_popup(rootStore, "panda");
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <LinkContainer
                    name="헝그리판다"
                    subTitle="배달매니저에서 바로 주문을 접수하려면 [연동하기]를 진행해주세요"
                    type="none"
                    onClick={() => {
                      showModal("HPD");
                    }}
                    unlinked={true}
                  />
                )}

                {/* 올때아아 */}
                {/* {settingStore.getShopApiSetting.oltteaahYn === "Y" ? (
                  <>
                    <LinkContainer
                      iconName="oltteaah"
                      name="올때아아"
                      subTitle={
                        expandedId.includes("oltteaah")
                          ? "배달매니저에서 주문을 바로 접수 & 처리할 수 있어요"
                          : "별도 프로그램에 로그인하면 배달매니저에서 주문을 접수할 수 있어요"
                      }
                      type="order"
                      onClick={() => handleExpanded("oltteaah")}
                      isExpanded={expandedId.includes("oltteaah")}
                    />
                    {expandedId.includes("oltteaah") && (
                      <div className="container">
                        <GuideRow
                          onClick={() => {
                            fn_bridge_agency_video(rootStore, "default");
                          }}
                          title="사전 세팅 후 사용하기"
                          subTitle="주문 접수 전 꼭 확인해주세요"
                          btnText="사용법 영상 보기"
                          iconName="video_ico"
                        />
                        <GuideRow
                          onClick={() => {
                            layerStore.setDlvConnectTypeView("OTA");
                            modalStore.openModal("DeliveryConnectBusiness", {});
                          }}
                          title="올때아아 연동 설정 관리"
                          subTitle="올때아아 가게 연동을 추가하거나 해제할 수 있어요"
                          btnText="연동설정"
                          iconName="setting_ico"
                        />
                        <GotoSiteContainer
                          id="oltteaah"
                          name="올때아아"
                          onClick={() => {
                            fn_bridge_agency_popup(rootStore, "oltteaah");
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <LinkContainer
                    name="올때아아"
                    subTitle="배달매니저에서 바로 주문을 접수하려면 [연동하기]를 진행해주세요"
                    type="none"
                    onClick={() => {
                      showModal("OTA");
                    }}
                    unlinked={true}
                  />
                )} */}

                {(paymentChannelList ?? []).map(item => {
                  return (
                    <div key={item.id}>
                      <>
                        <LinkContainer
                          iconName={item.name}
                          name={item.name}
                          subTitle="배달매니저에서 주문을 바로 접수 & 처리할 수 있어요"
                          type="order"
                          onClick={() => handleExpanded(item.id)}
                          isExpanded={expandedId?.includes(item.id)}
                        />
                        {expandedId?.includes(item.id) && (
                          <div className="container">
                            <GuideRow
                              title="사전 세팅 후 사용하기"
                              subTitle="주문 접수 전 꼭 확인해주세요"
                              onClick={() => {
                                fn_bridge_agency_video(rootStore, "default");
                                analyticeEvent("setting", "preSettingGuide", searchStore.getShopCd);
                              }}
                              btnText="사용법 영상 보기"
                              iconName="video_ico"
                            />
                            <GotoSiteContainer
                              id={item.id}
                              name={item.name}
                              onClick={() => {
                                fn_bridge_agency_popup(rootStore, item.id);
                                analyticeEvent("setting", "goToSite", searchStore.getShopCd);
                              }}
                            />
                          </div>
                        )}
                      </>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="list_none print">
                <h5 className="tit">
                  <span className="print">배달매니저 유료 요금제로 전환이 필요해요</span>
                </h5>
                <p className="print">고객센터 1544-3583로 연락주세요 (영업일 09:30~18:00)</p>
              </div>
            )
          ) : (
            // 기본
            <>
              <div className="basic_alert">
                <div>
                  <div className="bar"></div>
                  <div>
                    <p>
                      배달매니저 <strong>기본 연동</strong> <strong>사용법 영상을 확인</strong>하세요
                    </p>
                    <p className="!text-text-secondary !text-sm">
                      ※ 기본 연동 중에는 배달매니저에서 배달앱 매출을 합산할 수 있습니다.
                    </p>
                  </div>
                </div>
                <button
                  className="button"
                  onClick={() => {
                    fn_bridge_connect_video(rootStore, "basic");
                  }}
                >
                  <span>사용법 영상</span>
                  <div className="img"></div>
                </button>
              </div>

              {/* 기본 리스트 */}
              <div className="lists">
                {ORDER_CONNECT_CONFIGS.map(data => {
                  return (
                    <LinkContainer
                      isBasic={true}
                      key={data.name}
                      name={data.name}
                      subTitle={data.sub}
                      type={data.state}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default inject("rootStore")(observer(SettingDefaultOrderView));
